import React from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { Column, Line, Radar } from '@ant-design/charts';
import { MessageOutlined, RightOutlined } from '@ant-design/icons';
import {
  COMMUNICATION_CATEGORY_LABELS,
  COMMUNICATION_SOURCE_LABELS,
  COMMUNICATION_TAG_LABELS,
  WIDE_PROFILE_FIELD_KEYS,
  profileTimeText,
  textOrDash,
  timelinePresentation,
  timelineSummary,
  timeText,
} from './SimpleWorkspaceSupport';
import GoalInsightPanel from './GoalInsightPanel';
import TrainingPulsePanel from './TrainingPulsePanel';
import MemberTagPanel from './MemberTagPanel';
import CurrentPlanPanel from './CurrentPlanPanel';
import CommunicationProcessingPanel from './CommunicationProcessingPanel';
import CourseProcessingPanel from './CourseProcessingPanel';
import styles from './SimpleWorkspace.less';

const { Paragraph, Text } = Typography;
const chartAxis = {
  label: { style: { fill: '#64748b' } },
  line: { style: { stroke: '#d7e0e8' } },
  tickLine: { style: { stroke: '#d7e0e8' } },
  grid: { line: { style: { stroke: '#e8edf2', lineDash: [3, 3] } } },
};
const chartLegend = {
  position: 'top-right',
  itemName: { style: { fill: '#475569' } },
};

export default function SimpleWorkspaceOverview({ model, actions }) {
  const {
    snapshot,
    profile,
    memberDisplayName,
    memberPhone,
    memberBasicValues,
    additionalProfileFields,
    monthlyTrainingSummary,
    evaluatedCapabilityCount,
    capabilityAccounts,
    communicationLoading,
    communicationRows,
    communicationSourceCounts,
    unavailableMonthOffsets,
    monthlyProgress,
    monthlyProgressLoading,
    monthlyTrainingTrend,
    hasWeeklyTraining,
    weeklyTrainingData,
    weeklyTrainingSummary,
    bodyMetricTrends,
    weightTrendData,
    circumferenceTrendData,
    radarData,
    timelineLoading,
    timelineEvidence,
    availableTrainingContextCards,
  } = model;
  const memberInitial =
    memberDisplayName === '-' ? '会' : memberDisplayName.slice(0, 1);

  return (
    <Card
      bordered={false}
      className={styles.profileShell}
      title={
        <div className={styles.memberHeading}>
          <span className={styles.memberAvatar}>{memberInitial}</span>
          <span>
            <span className={styles.memberName}>{memberDisplayName}</span>
            <Text type="secondary" className={styles.memberSubtitle}>
              训练档案与个人情况
            </Text>
          </span>
          <span className={styles.memberTags}>
            {profile?.sportTarget && (
              <Tag color="volcano">目标：{profile.sportTarget}</Tag>
            )}
            <Tag color="red">会员 ID：{snapshot.memberId}</Tag>
            {memberPhone && <Tag>手机号：{memberPhone}</Tag>}
          </span>
        </div>
      }
    >
      <Row gutter={[16, 16]} className={styles.profileGrid}>
        <Col xs={24} xl={10}>
          <Card
            size="small"
            bordered={false}
            title="基本信息"
            className={styles.infoCard}
          >
            <Descriptions size="small" column={{ xs: 1, sm: 2 }}>
              <Descriptions.Item label="会员 ID">
                {snapshot.memberId}
              </Descriptions.Item>
              <Descriptions.Item label="会员昵称">
                {memberDisplayName}
              </Descriptions.Item>
              <Descriptions.Item label="性别">
                {textOrDash(memberBasicValues.gender)}
              </Descriptions.Item>
              <Descriptions.Item label="年龄">
                {textOrDash(memberBasicValues.age)}
              </Descriptions.Item>
              <Descriptions.Item label="生日">
                {textOrDash(profile?.birthDay)}
              </Descriptions.Item>
              <Descriptions.Item label="身高">
                {textOrDash(memberBasicValues.height)}
              </Descriptions.Item>
              <Descriptions.Item label="体重">
                {textOrDash(memberBasicValues.weight)}
              </Descriptions.Item>
              <Descriptions.Item label="档案更新时间">
                {profileTimeText(profile?.questionTime)}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
        <Col xs={24} xl={14}>
          <Card
            size="small"
            bordered={false}
            title="目标与身体情况"
            className={styles.infoCard}
          >
            <Descriptions
              size="small"
              column={{ xs: 1, sm: 2 }}
              contentStyle={{ wordBreak: 'break-word' }}
            >
              <Descriptions.Item label="运动目标">
                {textOrDash(profile?.sportTarget)}
              </Descriptions.Item>
              <Descriptions.Item label="运动习惯">
                {textOrDash(profile?.exercise)}
              </Descriptions.Item>
              <Descriptions.Item label="每日静坐">
                {textOrDash(profile?.sedentary)}
              </Descriptions.Item>
              <Descriptions.Item label="会员特征" span={2}>
                {textOrDash(profile?.feature)}
              </Descriptions.Item>
              <Descriptions.Item label="健康与伤病情况" span={2}>
                {textOrDash(profile?.symptom)}
              </Descriptions.Item>
              {additionalProfileFields.map((item) => (
                <Descriptions.Item
                  key={item.key}
                  label={item.key}
                  span={WIDE_PROFILE_FIELD_KEYS.has(item.key) ? 2 : 1}
                >
                  {textOrDash(item.value)}
                </Descriptions.Item>
              ))}
            </Descriptions>
          </Card>
        </Col>
      </Row>
      <MemberTagPanel key={snapshot.memberId} memberId={snapshot.memberId} />
      <GoalInsightPanel model={model} actions={actions} />
      <CurrentPlanPanel key={snapshot.memberId} memberId={snapshot.memberId} />
      <TrainingPulsePanel
        monthlyProgress={monthlyProgress}
        loading={monthlyProgressLoading}
      />

      <Card
        size="small"
        bordered={false}
        title={
          <span className={styles.communicationTitle}>
            <MessageOutlined />
            训练沟通观察
          </span>
        }
        extra={
          <Button
            type="link"
            size="small"
            onClick={() => actions.changeActiveTab('communication')}
          >
            查看全部
          </Button>
        }
        className={`${styles.infoCard} ${styles.communicationOverview}`}
      >
        <Spin spinning={communicationLoading && !communicationRows.length}>
          {communicationRows.length ? (
            <Row gutter={[20, 16]}>
              <Col xs={24} lg={6}>
                <div className={styles.communicationSourceSummary}>
                  <Text type="secondary">近期已提取</Text>
                  <div className={styles.communicationCount}>
                    {communicationRows.length}
                    <small>条训练信息</small>
                  </div>
                  <Space size={[4, 6]} wrap>
                    <Tag color="green">
                      企微 {communicationSourceCounts.workwx_chat || 0}
                    </Tag>
                    <Tag color="blue">
                      1V1{' '}
                      {(communicationSourceCounts.guided_course || 0) +
                        (communicationSourceCounts.live_coaching || 0)}
                    </Tag>
                  </Space>
                </div>
              </Col>
              <Col xs={24} lg={18}>
                <div className={styles.communicationPreviewList}>
                  {communicationRows.slice(0, 3).map((item) => (
                    <div
                      key={item.evidenceId}
                      className={styles.communicationPreviewItem}
                    >
                      <div className={styles.communicationPreviewHeader}>
                        <Space size={6} wrap>
                          <Tag
                            color={
                              item.sourceType === 'workwx_chat'
                                ? 'green'
                                : 'blue'
                            }
                          >
                            {COMMUNICATION_SOURCE_LABELS[item.sourceType] ||
                              '训练沟通'}
                          </Tag>
                          <Text strong>
                            {COMMUNICATION_TAG_LABELS[item.tagType] ||
                              COMMUNICATION_CATEGORY_LABELS[item.category] ||
                              '训练信息'}
                          </Text>
                        </Space>
                        <Text type="secondary">
                          {timeText(item.occurredAt)}
                        </Text>
                      </div>
                      <Paragraph
                        ellipsis={{ rows: 2 }}
                        className={styles.communicationPreviewText}
                      >
                        {textOrDash(item.summary)}
                      </Paragraph>
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="暂未提取到企微或 1V1 训练沟通信息"
            />
          )}
        </Spin>
      </Card>

      <section className={styles.processingSection}>
        <div className={styles.processingHeading}>
          <div>
            <Text className={styles.sectionTitle}>数据处理诊断</Text>
            <Text type="secondary" className={styles.sectionSubtitle}>
              按需排查沟通清洗、训练事实同步和课程评估链路，不参与会员训练结论。
            </Text>
          </div>
          <Tag className={styles.processingTag}>按需查询</Tag>
        </div>
        <div className={styles.processingGrid}>
          <CommunicationProcessingPanel
            key={`communication-processing-${snapshot.memberId}`}
            memberId={snapshot.memberId}
          />
          <CourseProcessingPanel
            key={`course-processing-${snapshot.memberId}`}
            memberId={snapshot.memberId}
          />
        </div>
      </section>

      <div className={styles.dashboardHeading}>
        <div>
          <Text className={styles.sectionTitle}>趋势与证据</Text>
          <Text type="secondary" className={styles.sectionSubtitle}>
            把训练投入、规律性、能力表现与身体变化放到同一观察框架中。
          </Text>
        </div>
      </div>
      <Row gutter={[16, 16]} className={styles.dashboardGrid}>
        <Col xs={24} xl={16} className={styles.trendPanel}>
          <Card
            size="small"
            bordered={false}
            title="近 6 个月训练趋势"
            extra={
              unavailableMonthOffsets.length > 0 ? (
                <Text type="secondary" className={styles.cardMeta}>
                  {unavailableMonthOffsets.length} 个月数据暂不可用
                </Text>
              ) : (
                <Text type="secondary" className={styles.cardMeta}>
                  单位：分钟
                </Text>
              )
            }
            className={`${styles.infoCard} ${styles.dashboardCard}`}
          >
            {monthlyProgress.length > 0 && (
              <div className={styles.trainingFactRow}>
                <div className={styles.trainingFact}>
                  <Text type="secondary">累计训练</Text>
                  <div>
                    <Text strong className={styles.trainingFactValue}>
                      {monthlyTrainingSummary.minutes}
                    </Text>
                    <Text type="secondary">分钟</Text>
                  </div>
                </div>
                <div className={styles.trainingFact}>
                  <Text type="secondary">训练天数</Text>
                  <div>
                    <Text strong className={styles.trainingFactValue}>
                      {monthlyTrainingSummary.days}
                    </Text>
                    <Text type="secondary">天</Text>
                  </div>
                </div>
                <div className={styles.trainingFact}>
                  <Text type="secondary">训练记录</Text>
                  <div>
                    <Text strong className={styles.trainingFactValue}>
                      {monthlyTrainingSummary.sessions}
                    </Text>
                    <Text type="secondary">次</Text>
                  </div>
                </div>
              </div>
            )}
            {monthlyProgressLoading ? (
              <div className={styles.chartLoading}>
                <Spin />
              </div>
            ) : monthlyTrainingTrend.length > 0 ? (
              <Line
                data={monthlyTrainingTrend}
                xField="month"
                yField="minutes"
                seriesField="type"
                height={220}
                smooth
                point={{ size: 4, shape: 'circle' }}
                color={['#e53935', '#5c6bc0']}
                meta={{ minutes: { min: 0, nice: true, alias: '训练分钟' } }}
                xAxis={chartAxis}
                yAxis={chartAxis}
                legend={chartLegend}
                tooltip={{ showMarkers: true }}
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="暂无月度训练趋势"
              />
            )}
          </Card>
        </Col>

        <Col xs={24} xl={8} className={styles.weeklyPanel}>
          <Card
            size="small"
            bordered={false}
            title="本周训练与运动时长"
            extra={
              <Text type="secondary" className={styles.cardMeta}>
                单位：分钟
              </Text>
            }
            className={`${styles.infoCard} ${styles.dashboardCard}`}
          >
            {hasWeeklyTraining && (
              <div className={styles.trainingFactRow}>
                <div className={styles.trainingFact}>
                  <Text type="secondary">上课时长</Text>
                  <div>
                    <Text strong className={styles.trainingFactValue}>
                      {weeklyTrainingSummary.courseMinutes}
                    </Text>
                    <Text type="secondary">分钟</Text>
                  </div>
                </div>
                <div className={styles.trainingFact}>
                  <Text type="secondary">运动记录时长</Text>
                  <div>
                    <Text strong className={styles.trainingFactValue}>
                      {weeklyTrainingSummary.sportMinutes}
                    </Text>
                    <Text type="secondary">分钟</Text>
                  </div>
                </div>
                <div className={styles.trainingFact}>
                  <Text type="secondary">活跃天数</Text>
                  <div>
                    <Text strong className={styles.trainingFactValue}>
                      {weeklyTrainingSummary.activeDays}
                    </Text>
                    <Text type="secondary">天</Text>
                  </div>
                </div>
              </div>
            )}
            {hasWeeklyTraining ? (
              <Column
                data={weeklyTrainingData}
                xField="day"
                yField="minutes"
                seriesField="type"
                isStack
                height={220}
                color={['#e53935', '#5c6bc0']}
                meta={{ minutes: { min: 0, nice: true, alias: '训练分钟' } }}
                xAxis={chartAxis}
                yAxis={chartAxis}
                legend={chartLegend}
                columnStyle={{ radius: [5, 5, 0, 0] }}
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="本周暂无课程训练或运动记录"
              />
            )}
          </Card>
        </Col>

        <Col xs={24} xl={10} className={styles.capabilityPanel}>
          <Card
            size="small"
            bordered={false}
            title="运动能力概览"
            extra={
              <Text type="secondary" className={styles.cardMeta}>
                {capabilityAccounts.length
                  ? `${evaluatedCapabilityCount} / ${capabilityAccounts.length} 项已评分`
                  : '暂无能力数据'}
              </Text>
            }
            className={`${styles.infoCard} ${styles.dashboardCard}`}
          >
            {radarData.length ? (
              <Radar
                data={radarData}
                xField="dimension"
                yField="score"
                meta={{ score: { min: 1, max: 10 } }}
                area={{ style: { fillOpacity: 0.18 } }}
                point={{ size: 3 }}
                color="#e53935"
                height={280}
                xAxis={{
                  label: { style: { fill: '#64748b' } },
                  grid: { line: { style: { stroke: '#d7e0e8' } } },
                }}
                yAxis={{
                  label: false,
                  grid: { line: { style: { stroke: '#d7e0e8' } } },
                }}
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="暂无运动能力评分"
              />
            )}
          </Card>
        </Col>
        <Col xs={24} xl={14} className={styles.recentActivityPanel}>
          <Card
            size="small"
            bordered={false}
            title="近期活动"
            extra={
              <Button
                type="link"
                size="small"
                onClick={() => actions.changeActiveTab('archive')}
              >
                查看时间轴
              </Button>
            }
            className={`${styles.infoCard} ${styles.dashboardCard}`}
          >
            <Spin spinning={timelineLoading && !timelineEvidence.length}>
              {timelineEvidence.length ? (
                <div className={styles.recentActivityList}>
                  {timelineEvidence.slice(0, 4).map((record) => {
                    const presentation = timelinePresentation(record);
                    return (
                      <button
                        type="button"
                        key={record.evidenceId}
                        className={styles.recentActivityItem}
                        onClick={() => actions.openTimelineEvent(record)}
                      >
                        <span
                          className={styles.recentActivityDot}
                          style={{ backgroundColor: presentation.color }}
                        />
                        <span className={styles.recentActivityContent}>
                          <span className={styles.recentActivityTitle}>
                            <Text strong>{presentation.title}</Text>
                            <Tag color={presentation.color}>
                              {presentation.category}
                            </Tag>
                          </span>
                          <Text
                            type="secondary"
                            ellipsis
                            className={styles.recentActivitySummary}
                          >
                            {timelineSummary(record, {
                              includeCompletion: false,
                            })}
                          </Text>
                        </span>
                        <span className={styles.recentActivityMeta}>
                          <Text type="secondary">
                            {timeText(record.occurredAt)}
                          </Text>
                          <RightOutlined />
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="暂无已归档的近期活动"
                />
              )}
            </Spin>
          </Card>
        </Col>
      </Row>

      {(availableTrainingContextCards.length > 0 ||
        bodyMetricTrends.length > 0) && (
        <Card
          size="small"
          bordered={false}
          title="营养、恢复与影响因素"
          className={`${styles.infoCard} ${styles.contextCard}`}
          extra={
            <Text type="secondary" className={styles.contextHint}>
              只作为训练判断背景，不计入运动能力得分
            </Text>
          }
        >
          <div className={styles.contextGrid}>
            {availableTrainingContextCards.map((item) => {
              const maxValue = Math.max(...item.dailyStats, 0);
              return (
                <div
                  key={item.key}
                  className={`${styles.contextItem} ${
                    styles[`contextItem-${item.tone}`]
                  }`}
                >
                  <div className={styles.contextItemHeader}>
                    <Text strong>{item.label}</Text>
                    <Tag>已有记录</Tag>
                  </div>
                  <div className={styles.contextIndicatorList}>
                    {item.indicators.length ? (
                      item.indicators.map((indicator) => (
                        <div
                          key={`${item.key}-${indicator.name}`}
                          className={styles.contextIndicator}
                        >
                          <Text type="secondary">{indicator.name}</Text>
                          <Text strong>
                            {textOrDash(indicator.value)}
                            {indicator.unit || ''}
                          </Text>
                        </div>
                      ))
                    ) : (
                      <Text
                        type="secondary"
                        className={styles.contextStatusText}
                      >
                        本周已有相关记录
                      </Text>
                    )}
                  </div>
                  {maxValue > 0 && (
                    <div
                      className={styles.contextBars}
                      aria-label={`${item.label}近 7 日记录`}
                    >
                      {item.dailyStats.map((value, index) => (
                        <span
                          key={`${item.key}-${index}`}
                          style={{
                            height: `${Math.max(
                              12,
                              (value / maxValue) * 100,
                            )}%`,
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {bodyMetricTrends.length > 0 && (
            <>
              <div className={styles.bodyMetricSummary}>
                {bodyMetricTrends.map((metric) => {
                  const change = Number(metric.change) || 0;
                  return (
                    <div
                      key={metric.metricCode}
                      className={styles.bodyMetricSummaryItem}
                    >
                      <Text type="secondary">{metric.metricName}</Text>
                      <Text strong>
                        {textOrDash(metric.latestValue)} {metric.unit}
                      </Text>
                      <Text type="secondary" className={styles.metricDelta}>
                        较首条 {change > 0 ? '+' : ''}
                        {change.toFixed(1)} {metric.unit}
                      </Text>
                    </div>
                  );
                })}
              </div>
              <div className={styles.bodyMetricTrendGrid}>
                <Card
                  size="small"
                  bordered={false}
                  title="体重变化"
                  className={styles.bodyMetricTrendCard}
                >
                  {weightTrendData.length ? (
                    <Line
                      data={weightTrendData}
                      xField="date"
                      yField="value"
                      height={220}
                      smooth
                      point={{ size: 4 }}
                      color="#e53935"
                      meta={{ value: { nice: true, alias: '体重（kg）' } }}
                      xAxis={chartAxis}
                      yAxis={chartAxis}
                    />
                  ) : (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="暂无体重变化记录"
                    />
                  )}
                </Card>
                <Card
                  size="small"
                  bordered={false}
                  title="身体围度变化"
                  className={styles.bodyMetricTrendCard}
                >
                  {circumferenceTrendData.length ? (
                    <Line
                      data={circumferenceTrendData}
                      xField="date"
                      yField="value"
                      seriesField="metric"
                      height={220}
                      smooth
                      point={{ size: 3 }}
                      meta={{ value: { nice: true, alias: '围度（cm）' } }}
                      xAxis={chartAxis}
                      yAxis={chartAxis}
                      legend={{
                        ...chartLegend,
                        position: 'top',
                      }}
                    />
                  ) : (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="暂无腰围、腿围等身体围度记录"
                    />
                  )}
                </Card>
              </div>
            </>
          )}
        </Card>
      )}
    </Card>
  );
}

SimpleWorkspaceOverview.propTypes = {
  model: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
};
